/**
 * 要件定義のレビュー操作の配線（成果物 1 つぶんの `ReviewActions` を組み立てて返す）。
 *
 * `SpecRequirementsView` から切り出してある。**1 ファイル 500 行（コード行）の上限に収める
 * ための分割**で、レビューの 4 つの操作・対象ごとの処理中/失敗の絞り込み・指摘を出した人の
 * 表示名の引き当ては、成果物の編集とは独立した語彙なのでここで切れる。レビュー操作の入口が
 * 2 本に分かれたわけではなく、画面に出すのは引き続き `SpecRequirementsView` 1 本。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の成果物ごとのレビュー操作（依頼・指摘の解決・DLE の承認と差し戻し）を組み立てて返す
 * @graph-connects ReviewActions [calls] レビュー操作
 * @graph-connects useMemberList [calls] 指摘を出した人の表示名の引き当て
 */

import { useMemo, type ReactNode } from 'react';
import {
  useRequestSpecReview,
  useResolveSpecFindings,
  useRevokeSpecApproval,
  useSubmitSpecReview,
} from '../../../hooks/useSpecRequirements.js';
import { useMemberList } from '../../../hooks/useMember.js';
import { buildNicknameByEmail } from '../../../lib/displayName.js';
import { ReviewActions } from './ReviewActions.js';
import type { SpecReviewState } from '../../../lib/api/spec-requirements.js';

/**
 * 成果物 1 つぶんのレビュー操作を組み立てる関数。
 *
 * 業務フローの節（`FlowSections`）にもそのまま渡すので、形を共有の型にしておく。
 * @graph-connects none
 */
export type ReviewActionsFor = (
  targetId: string,
  label: string,
  version: number,
  review: SpecReviewState | null,
) => ReactNode;

/**
 * 案件 1 件ぶんのレビュー操作を組み立てる。
 *
 * 4 つの操作は案件内の全てのレビュー対象（業務フローと案件に 1 つの成果物 5 つ）で共有されて
 * いる。処理中・失敗の表示を対象の `targetId` で絞り、**他の対象の操作中表示・失敗表示が
 * 無関係な対象に漏れないようにする**。
 * @graph-connects ReviewActions [calls] レビュー操作
 * @graph-connects useMemberList [calls] 指摘を出した人の表示名の引き当て
 */
export function useSpecReviewActions(pjKey: string): ReviewActionsFor {
  const requestReview = useRequestSpecReview(pjKey);
  const resolveFindings = useResolveSpecFindings(pjKey);
  const submitReview = useSubmitSpecReview(pjKey);
  const revokeApproval = useRevokeSpecApproval(pjKey);

  // 指摘に入っているのは提出時の actor（email）だけなので、表示名はここで引く
  const { data: memberList } = useMemberList();
  const nicknameByEmail = useMemo(
    () => buildNicknameByEmail([...(memberList?.engineers ?? []), ...(memberList?.qa ?? [])]),
    [memberList],
  );

  const stateFor = (targetId: string): { busy: boolean; error: unknown } => {
    const operations: [string | undefined, { isPending: boolean; error: unknown }][] = [
      [requestReview.variables, requestReview],
      [resolveFindings.variables?.targetId, resolveFindings],
      [submitReview.variables?.targetId, submitReview],
      [revokeApproval.variables, revokeApproval],
    ];
    const operation = operations.find(([id]) => id === targetId)?.[1];
    return { busy: operation?.isPending ?? false, error: operation?.error ?? null };
  };

  return (targetId, label, version, review) => (
    <ReviewActions
      pjKey={pjKey}
      targetId={targetId}
      label={label}
      version={version}
      review={review}
      nicknameByEmail={nicknameByEmail}
      busy={stateFor(targetId).busy}
      error={stateFor(targetId).error}
      onRequest={(id) => {
        requestReview.mutate(id);
      }}
      onResolve={(id, resolutions) => {
        resolveFindings.mutate({ targetId: id, resolutions });
      }}
      onSubmit={(id, input) => {
        submitReview.mutate({ targetId: id, input });
      }}
      onRevoke={revokeApproval.mutate}
    />
  );
}
